import { keepPreviousData, useQuery } from "@tanstack/vue-query";
import { type MaybeRef, toValue } from "vue";

import { pb, pbCollectionNames } from "#api/pocketbase";
import type { ApiExpand, PaginationInputRefs } from "#api/types";
import { getApiExpansions, getPaginationInputArgs, joinApiFilters } from "#api/utils";
import type { SubmissionTier } from "#modules/submission/submission.types";
import type { Season, WithActiveSeasonData } from "./season.types";

export interface SeasonsPaginatedArgs extends PaginationInputRefs {
  search?: MaybeRef<string>;
}

// NOTE: Query keys used for invalidation should require all arguments, as 'undefined' in query key
//         prevents invalidating any queries with similar key but that do provide args for that argument!
export const seasonKeys = {
  all: ["seasons/all"],
  allPaginated: (args: SeasonsPaginatedArgs) => [...seasonKeys.all, args],
  current: ["seasons/current"],
} as const;

export const useSeasonsQuery = (args: SeasonsPaginatedArgs) => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: seasonKeys.allPaginated(args),
    queryFn: async () => {
      const seasonSearchFilter = toValue(args.search) ? `name ~ '${toValue(args.search)}'` : "";
      return pb
        .collection(pbCollectionNames.seasons)
        .getList<
          Season & ApiExpand<{ submissionTier_via_season?: SubmissionTier[] }>
        >(...getPaginationInputArgs(args), {
          expand: getApiExpansions("season", ["submissionTier_via_season"]),
          filter: joinApiFilters(seasonSearchFilter),
          sort: "-submissionsEnd",
        });
    },
  });
};

/** Get current/active seasons (may be multiple!) */
export const useCurrentSeasonsQuery = () => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: seasonKeys.current,
    queryFn: async () => {
      return pb.send<(Season & WithActiveSeasonData)[]>("/api/season/current", {});
    },
  });
};
